"use client";

import { Slider } from "antd";
import Container from "../container";
import styles from "./organizers.module.css";
import { useState, useMemo } from "react";

const formatIndianNumber = (num) => {
  if (num >= 1e7) {
    return (num / 1e7).toFixed(2).replace(/\.00$/, "") + " Cr";
  }
  if (num >= 1e5) {
    return (num / 1e5).toFixed(2).replace(/\.00$/, "") + " Lakh";
  }
  return Math.round(num).toLocaleString("en-IN");
};

const calcPercentage = (base, compare) =>
  base ? Math.round((compare / base) * 100) : 0;

const renderMessage = (template, amount, percentage, isSave) => {
  const formattedAmount = formatIndianNumber(Math.round(amount));
  if (template) {
    return template
      .replace(/\{amount\}/g, formattedAmount)
      .replace(/\{percentage\}/g, `${percentage}`);
  }
  return isSave
    ? `You save ₹ ${formattedAmount} (${percentage}%)`
    : `You pay additional ₹ ${formattedAmount} (${percentage}%)`;
};

export function PricingComparison({ data }) {
  const platforms = data?.platforms || [];

  const [sceneCount, setSceneCount] = useState(
    data?.scene_default ?? data?.scene_min ?? 500,
  );
  const [eventCount, setEventCount] = useState(
    data?.event_default ?? data?.event_min ?? 1000,
  );

  const results = useMemo(() => {
    const base = sceneCount * eventCount;

    const withEarnings = platforms
      .filter((p) => p?.name)
      .map((p) => {
        let displayName = p.name;
        const lowerName = p.name.toLowerCase();

        if (lowerName === "bookmyshow" || lowerName === "book my show") {
          displayName = "Other Platform";
        } else if (lowerName === "district") {
          displayName = "Other Platform";
        } else if (lowerName === "zomato") {
          displayName = "Other Platform";
        } else if (lowerName === "paytm" || lowerName === "paytm insider") {
          displayName = "Other Platform";
        } else if (lowerName === "insider") {
          displayName = "Other Platform";
        } else if (lowerName !== "tykkit") {
          displayName = "Other Platform";
        }

        const bgColor = p.bg_color || "#ffffff";
        const textColor = p.text_color || "#000000";

        return {
          ...p,
          originalName: p.name,
          name: displayName,
          bgColor,
          textColor,
          earnValue: base * (Number(p.rate) || 0),
        };
      })
      .sort((a, b) => b.earnValue - a.earnValue);

    const top = withEarnings[0];
    const runnerUp = withEarnings[1];

    return withEarnings.map((p, idx) => {
      if (idx === 0) {
        const diff = runnerUp ? p.earnValue - runnerUp.earnValue : 0;
        const percentage = calcPercentage(p.earnValue, diff);
        return {
          ...p,
          earn: formatIndianNumber(Math.round(p.earnValue)),
          message: renderMessage(p.message, diff, percentage, true),
          type: "save",
        };
      }

      const diff = top.earnValue - p.earnValue;
      const percentage = calcPercentage(top.earnValue, diff);
      return {
        ...p,
        earn: formatIndianNumber(Math.round(p.earnValue)),
        message: renderMessage(p.message, diff, percentage, false),
        type: "loss",
      };
    });
  }, [sceneCount, eventCount, platforms]);

  if (results.length === 0) return null;

  return (
    <Container>
      <section
        className={`py-12 relative my-12 lg:!mt-[47px] lg:!mb-[80px] ${styles.zigzagContainer} bg-[#f0f9ff] md:bg-transparent rounded-3xl md:rounded-none px-5 sm:px-7 md:px-0`}
      >
        <svg
          className="absolute inset-0 w-full h-full -z-10 md:block hidden"
          viewBox="0 0 1156 692"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.25431 20.5392C-2.93053 32.8637 -1.48407 47.8003 9.11153 59.3771L85.2211 140.417L9.11153 221.081C-2.93053 234.153 -2.93053 252.078 9.11153 265.148L85.2211 346.188L9.11153 426.852C-2.93053 440.296 -2.93053 457.849 9.11153 470.919L85.2211 551.957L9.11152 632.997C-1.48408 644.2 -2.93054 659.136 5.2543 671.835C13.4509 684.159 30.3027 692 48.1306 692H1107.83C1126.13 692 1142.51 684.159 1150.7 671.835C1158.89 659.136 1157.45 644.2 1146.84 632.997L1071.22 551.957L1146.84 470.919C1158.89 457.849 1158.89 440.296 1146.84 426.852L1071.22 346.188L1146.84 265.148C1158.89 252.078 1158.89 234.153 1146.84 221.081L1071.22 140.417L1146.84 59.3771C1153.11 52.6553 1156 45.1882 1156 37.3449C1156 31.3685 1154.07 25.7681 1150.7 20.5393C1142.51 7.84326 1126.13 9.96632e-06 1107.83 9.79714e-06L48.1306 0C30.3027 -7.6338e-06 13.4509 7.84325 5.25431 20.5392Z"
            fill="#F0F9FF"
          />
          <path
            d="M192.673 112.032H915.196V597.516H192.673V112.032Z"
            fill="#F0F9FF"
          />
        </svg>

        <div className="flex flex-col justify-center items-center">
          {(data?.title || data?.description) && (
            <div className="text-center max-w-[650px] mx-auto">
              {data?.title ? (
                <h2 className={styles.prizingTitle}>{data.title}</h2>
              ) : null}
              {data?.description ? (
                <p className={styles.prizingDesc}>{data.description}</p>
              ) : null}
            </div>
          )}

          <div
            className={`mt-10 max-w-[600px] mx-auto w-full flex flex-col gap-6 ${styles.sliderWrapper}`}
          >
            <div>
              <div
                className={`flex justify-between items-center ${styles.sliderBkl}`}
              >
                <span
                  className={`text-gray-700 font-medium ${styles.sliderTitle}`}
                >
                  {data?.scene_label || "What's Your Scene?"}
                </span>
                <span
                  className={`font-semibold text-[#051C5B] ${styles.sliderPrize}`}
                >
                  ₹{sceneCount}
                </span>
              </div>
              <Slider
                min={data?.scene_min ?? 500}
                max={data?.scene_max ?? 50000}
                step={500}
                value={sceneCount}
                onChange={setSceneCount}
                trackStyle={{ background: data?.track_color || "#FF97D4" }}
                handleStyle={{ borderColor: data?.handle_color || "#22C55E" }}
              />
            </div>

            <div>
              <div
                className={`flex justify-between items-center ${styles.sliderBkl}`}
              >
                <span
                  className={`text-gray-700 font-medium ${styles.sliderTitle}`}
                >
                  {data?.event_label || "Attendees Per Events"}
                </span>
                <span
                  className={`font-semibold text-[#051C5B] ${styles.sliderPrize}`}
                >
                  {eventCount}
                </span>
              </div>
              <Slider
                min={data?.event_min ?? 100}
                max={data?.event_max ?? 10000}
                step={100}
                value={eventCount}
                onChange={setEventCount}
                trackStyle={{ background: data?.track_color || "#FF97D4" }}
                handleStyle={{ borderColor: data?.handle_color || "#22C55E" }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-4 xl:gap-6 mt-12 h-full w-full md:w-auto">
            {results.map((item, idx) => (
              <div
                key={idx}
                className="flex flex-col bg-white rounded-2xl h-full min-w-[200px] overflow-hidden"
              >
                <div
                  className="sm:px-4 sm:py-5 p-3 sm:block flex justify-between"
                  style={{
                    backgroundColor: item.bgColor,
                    color: item.textColor,
                  }}
                >
                  <h3
                    className={`sm:pb-4 lg:pb-10 ${styles.cardTitle}`}
                    style={{ color: item.textColor }}
                  >
                    {item.name}
                  </h3>
                  <div className="sm:text-left text-right">
                    <p
                      className={styles.cardDesc}
                      style={{ color: item.textColor }}
                    >
                      {item.subtext || "You earn"}
                    </p>
                    <p
                      className="text-xl sm:text-2xl font-bold"
                      style={{ color: item.textColor }}
                    >
                      ₹ {item.earn} {item.emoji}
                    </p>
                  </div>
                </div>

                <div
                  className={`mb-0  py-2 px-2 text-[13px] font-medium text-center ${
                    item.type === "save"
                      ? "bg-[#17b26a1f] text-[#17B26A]"
                      : "bg-[#f044381f] text-[#F04438]"
                  } rounded-b-2xl sm:rounded-b-none`}
                >
                  {item.message}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Container>
  );
}

export default PricingComparison;
